/**
 * TS-client voor de office-app risico-inbox (bundle-aware).
 *   GET  /api/risico/list      → bundles per Werk_Order, per sectie-status
 *   POST /api/risico/beoordeel → senior-poort: is dit echt een risico?
 *   POST /api/risico/draft     → AI-mail (gpt-5.6-sol)
 *   POST /api/risico/send      → Gmail-send + registratie + statusflip
 *   POST /api/risico/verwerk   → klant-antwoord verwerken
 */
import { supabase } from './supabase';

export interface RisicoBundleItem {
  wasserijItemId: string;
  wiNummer: string;
  kleedNaam: string;
  risicoTypes: string[];
  toelichting: string | null;
  /**
   * AI-gepolijste klantvriendelijke variant van `toelichting`. Wordt asynchroon
   * door Haiku gegenereerd direct na meld. Null = polish staat nog uit (mail
   * + client-page tonen dan de originele `toelichting`).
   */
  toelichtingKlantvriendelijk: string | null;
  fotosUrl: string | null;
  gemeldOp: string | null;
  /** Alleen gevuld in de wacht_klant-lijst (wachttijd-anker; null in het
   * SF-fallback-pad — gebruik dan gemeldOp). */
  mailVerstuurdOp?: string | null;
  /** @deprecated Magic-link vervalt in v2; alleen nog op legacy-rijen. */
  tokenExpiry?: string | null;
  /** Inbox-thread met de risico-mail; link-doel vanuit de wacht-sectie. */
  conversationId?: string | null;
  /** De risicomail kwam terug (bounce) en is niet opnieuw verstuurd (wacht_klant-lijst). */
  mailBounce?: { op: string; adres: string | null } | null;
  beoordeeldOp?: string | null;
  beoordeeldDoor?: string | null;
}

export interface RisicoBundle {
  workOrderId: string;
  workOrderNummer: string | null;
  klantNaam: string | null;
  klantEmail: string | null;
  anchorWasserijItemId: string;
  oudsteGemeldOp: string | null;
  items: RisicoBundleItem[];
}

export interface RisicoDraftKleed {
  wiNummer: string;
  kleedNaam: string;
  types: string[];
  toelichting: string | null;
}

export interface RisicoDraft {
  subject: string;
  body: string;
  model?: string;
  confidence?: number;
  confidence_reason?: string;
  kleden?: RisicoDraftKleed[];
  totaalKleden?: number;
}

async function authHeaders(): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession();
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  if (session?.access_token) h.Authorization = `Bearer ${session.access_token}`;
  return h;
}

async function getJson<T>(path: string): Promise<T> {
  const r = await fetch(path, { headers: await authHeaders() });
  if (!r.ok) {
    const err = await r.json().catch(() => ({ error: `Request failed (${r.status})` }));
    throw new Error(err.error || err.message || `Request failed (${r.status})`);
  }
  return r.json() as Promise<T>;
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const r = await fetch(path, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify(body),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({ error: `Request failed (${r.status})` }));
    throw new Error(err.error || err.message || `Request failed (${r.status})`);
  }
  return r.json() as Promise<T>;
}

/** De drie secties van /risicos. */
export type RisicoSectie = 'gemeld' | 'bevestigd' | 'wacht_klant';

export async function fetchRisicoBundles(
  status: RisicoSectie = 'gemeld'
): Promise<RisicoBundle[]> {
  const qs = status === 'gemeld' ? '' : `?status=${status}`;
  const { bundles } = await getJson<{ bundles: RisicoBundle[] }>(`/api/risico/list${qs}`);
  return bundles;
}

export async function fetchRisicoOpenCount(): Promise<number> {
  const bundles = await fetchRisicoBundles();
  return bundles.length;
}

export async function generateRisicoDraft(
  workOrderId: string,
  extraContext?: string
): Promise<RisicoDraft> {
  return postJson<RisicoDraft>('/api/risico/draft', { workOrderId, extraContext });
}

export interface SendRisicoMailPayload {
  workOrderId: string;
  subject: string;
  body: string;
  /** De onbewerkte AI-tekst, zodat de edit-afstand gemeten kan worden. */
  aiDraft?: string;
  extraContext?: string;
}

export async function sendRisicoMail(
  payload: SendRisicoMailPayload
): Promise<{
  success: boolean;
  risicoStatus?: string;
  itemsUpdated?: number;
  conversationId?: string | null;
  warning?: string;
}> {
  return postJson('/api/risico/send', payload);
}

export interface PolishResult {
  success: boolean;
  polished: string | null;
  cached?: boolean;
  fallbackToOriginal?: boolean;
  error?: string;
}

/**
 * Trigger AI-polish (of refresh) van Toelichting_Klantvriendelijk__c voor
 * een specifiek WI. `force=true` overschrijft een bestaande polish.
 */
export async function polishToelichting(
  wasserijItemId: string,
  force = false
): Promise<PolishResult> {
  return postJson<PolishResult>('/api/risico/polish-toelichting', {
    wasserijItemId,
    force,
  });
}

/**
 * Sla een handmatige edit op zonder AI-call. Schrijft direct naar
 * Toelichting_Klantvriendelijk__c via dezelfde endpoint (één audit-trail).
 */
export async function saveManualToelichting(
  wasserijItemId: string,
  manualText: string
): Promise<PolishResult & { manual?: boolean }> {
  return postJson<PolishResult & { manual?: boolean }>('/api/risico/polish-toelichting', {
    wasserijItemId,
    manualText,
  });
}

// ─── Specialist-override ──────────────────────────────────────────────────

export interface Specialist {
  userId: string;
  email: string;
  displayName: string;
}

export interface SpecialistAkkoordResult {
  success: boolean;
  risicoStatus?: string;
  wasserijItemId?: string;
  error?: string;
  errorCode?: string;
}

let specialistsCache: Specialist[] | null = null;

/**
 * Lijst van mogelijke specialisten (admin + medewerker rollen) voor de
 * specialist-akkoord dropdown. Module-level cache zodat per-bundle één call
 * volstaat — refresh treedt automatisch op bij page-reload.
 */
export async function fetchSpecialists(): Promise<Specialist[]> {
  if (specialistsCache) return specialistsCache;
  const { specialists } = await getJson<{ specialists: Specialist[] }>('/api/users/specialists');
  specialistsCache = specialists;
  return specialists;
}

export function clearSpecialistsCache(): void {
  specialistsCache = null;
}

/**
 * Specialist sluit een gemeld risico af zonder klant-mail. Vereist admin/
 * medewerker rol (callcenter wordt door de Vercel-route geweigerd).
 */
export async function markSpecialistAkkoord(
  wasserijItemId: string,
  specialistUserId: string,
  reden: string
): Promise<SpecialistAkkoordResult> {
  try {
    return await postJson<SpecialistAkkoordResult>('/api/risico/specialist-akkoord', {
      wasserijItemId,
      specialistUserId,
      reden,
    });
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Specialist-akkoord mislukt',
    };
  }
}

// ─── v2: beoordelen en verwerken ──────────────────────────────────────────

/**
 * Build-time flag voor het versimpelde risico-proces (drie secties, senior-
 * poort, verwerk-knoppen). Server-side gate is RISICO_V2_ENABLED; beide moeten
 * aan staan. Uit = de oude twee-secties-UI.
 */
export const RISICO_V2_ENABLED =
  (import.meta.env.VITE_RISICO_V2_ENABLED as string | undefined) === 'true';

export interface BeoordeelResult {
  success: boolean;
  wasserijItemId?: string;
  risicoStatus?: string;
  alReedsBeoordeeld?: boolean;
  itemVrijgegeven?: boolean;
  warning?: string;
  error?: string;
  errorCode?: string;
}

/**
 * De senior-poort: bevestig dat het echt een risico is (dan volgt de
 * klant-mail), of markeer het als geen risico (dan wordt het kleed vrijgegeven
 * voor reiniging).
 */
export async function beoordeelRisico(payload: {
  wasserijItemId: string;
  uitkomst: 'bevestig' | 'geen_risico';
  reden?: string;
}): Promise<BeoordeelResult> {
  try {
    return await postJson<BeoordeelResult>('/api/risico/beoordeel', payload);
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Beoordelen mislukt',
    };
  }
}

/** De vier knoppen bij "Wacht op klant", plus de generieke alternatief-vorm. */
export type VerwerkActie = 'akkoord' | 'lichtere_behandeling' | 'afvoeren' | 'retour' | 'zelfhalen' | 'alternatief';

export interface VerwerkResult {
  success: boolean;
  wasserijItemId?: string;
  risicoStatus?: string;
  alreadyDecided?: boolean;
  itemVrijgegeven?: boolean;
  warning?: string;
  error?: string;
  errorCode?: string;
}

/**
 * Verwerk het antwoord dat de klant per mail of telefonisch gaf. De notitie is
 * het citaat; het volledige antwoord staat in de gekoppelde inbox-thread.
 */
export async function verwerkKlantAntwoord(payload: {
  wasserijItemId: string;
  actie: VerwerkActie;
  productCode?: string;
  notitie: string;
}): Promise<VerwerkResult> {
  try {
    return await postJson<VerwerkResult>('/api/risico/verwerk', payload);
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Verwerken mislukt',
    };
  }
}

// ─── Beslis namens klant (v1, vervalt bij de opruiming) ───────────────────

/**
 * Build-time flag: knop tonen. Server-side gate is RISICO_NAMENS_KLANT_ENABLED.
 * @deprecated Opgevolgd door {@link RISICO_V2_ENABLED} en de verwerk-knoppen.
 */
export const NAMENS_KLANT_UI_ENABLED =
  (import.meta.env.VITE_RISICO_NAMENS_KLANT_ENABLED as string | undefined) === 'true';

export interface NamensKlantPayload {
  wasserijItemId: string;
  akkoord?: boolean;
  productCode?: string;
  reden: string;
}

export interface NamensKlantResult {
  success: boolean;
  wasserijItemId?: string;
  risicoStatus?: string;
  alreadyDecided?: boolean;
  warning?: string;
  error?: string;
  errorCode?: string;
}

/**
 * Office beslist namens de klant (tokenloos): Wacht_klant → Akkoord /
 * Niet_Akkoord / Alternatief_gekozen, met verplichte reden. Wordt met naam
 * en reden vastgelegd in het audit-spoor.
 */
export async function beslisNamensKlant(payload: NamensKlantPayload): Promise<NamensKlantResult> {
  try {
    return await postJson<NamensKlantResult>('/api/risico/beslis-namens-klant', payload);
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Namens-klant beslissen mislukt',
    };
  }
}
